import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../../../components/FileUpload';
import IconifyIcon from '../../../components/IconifyIcon';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button } from 'react-bootstrap';
import categoryRepository from '../../../services/repositories/category.repository';
import apiService from '../../../services/api.service';
import '../../../styles/products.scss';

const AddCategory = ({ isEdit = false, categoryId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    family_id: '',
    image: null,
    imageUrl: null,
    disabled_fes: false,
    disabled_casa: false,
    in_store_only: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [families, setFamilies] = useState([]);

  useEffect(() => {
    fetchFamilies();
    if (isEdit && categoryId) {
      fetchCategoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, categoryId]);

  const fetchFamilies = async () => {
    try {
      const response = await apiService.get('/families');
      const familiesData = response.data || response;
      setFamilies(Array.isArray(familiesData) ? familiesData : []);
    } catch (err) {
      console.error('Error fetching families:', err);
    }
  };

  const fetchCategoryData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await categoryRepository.getById(categoryId);
      const category = response.data || response;
      
      if (!category || !category.id) {
        throw new Error('Category data not found');
      }
      
      // Import buildImageUrl helper
      const { buildImageUrl } = await import('../../../utils/imageUrl');
      const imageUrl = buildImageUrl(category.image);
      
      setFormData({
        name: category.name || '',
        description: category.description || '',
        family_id: category.family_id || category.family?.id || '',
        image: null,
        imageUrl: imageUrl,
        disabled_fes: Boolean(category.disabled_fes),
        disabled_casa: Boolean(category.disabled_casa),
        in_store_only: Boolean(category.in_store_only),
      });
    } catch (err) {
      console.error('Error fetching category:', err);
      setError(err.message || 'Error loading category data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleImageChange = (files) => {
    if (files && files.length > 0) {
      setFormData(prev => ({ 
        ...prev, 
        image: files[0],
        imageUrl: null
      }));
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ 
      ...prev, 
      image: null,
      imageUrl: null 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submitData = {
        name: formData.name || '',
        description: formData.description || '',
        family_id: formData.family_id || null,
        image: formData.image,
        disabled_fes: formData.disabled_fes ? 1 : 0,
        disabled_casa: formData.disabled_casa ? 1 : 0,
        in_store_only: formData.in_store_only ? 1 : 0,
      };
      
      if (isEdit && categoryId) {
        await categoryRepository.update(categoryId, submitData);
      } else {
        await categoryRepository.create(submitData);
      }
      
      navigate('/admin/categories/category-list');
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde de la catégorie');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/categories/category-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données de la catégorie...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      {/* Image */}
      <Card className="mb-3">
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Image de la Catégorie</CardTitle>
        </CardHeader>
        <CardBody>
          {formData.image ? (
            <div className="text-center">
              <div className="position-relative d-inline-block">
                <img
                  src={formData.image.preview || formData.image}
                  alt="Catégorie"
                  className="img-fluid rounded border"
                  style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveImage}
                  style={{ zIndex: 10 }}
                >
                  <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                </Button>
              </div>
            </div>
          ) : formData.imageUrl ? (
            <div className="text-center">
              <div className="position-relative d-inline-block">
                <img
                  src={formData.imageUrl}
                  alt="Catégorie"
                  className="img-fluid rounded border"
                  style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveImage}
                  style={{ zIndex: 10 }}
                >
                  <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                </Button>
              </div>
              <div className="mt-3">
                <FileUpload 
                  title="Remplacer l'image" 
                  onFilesChange={handleImageChange}
                  maxFiles={1}
                />
              </div>
            </div>
          ) : (
            <FileUpload 
              title="" 
              onFilesChange={handleImageChange}
              maxFiles={1}
            />
          )}
        </CardBody>
      </Card>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Informations de la Catégorie</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="category-name" className="form-label">
                    Nom de la Catégorie <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="category-name"
                    name="name"
                    className="form-control"
                    placeholder="Nom de la catégorie"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="category-family" className="form-label">
                    Famille
                  </label>
                  <select
                    className="form-control"
                    id="category-family"
                    name="family_id"
                    value={formData.family_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner une famille</option>
                    {families.map(family => (
                      <option key={family.id} value={family.id}>
                        {family.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="category-description" className="form-label">
                    Description
                  </label>
                  <textarea
                    className="form-control bg-light-subtle"
                    id="category-description"
                    name="description"
                    rows={5}
                    placeholder="Description de la catégorie"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-5" style={{ paddingBottom: '12px' }}>
                  <label className="form-label">Disponibilité par région</label>
                  <Row className="g-4">
                    <Col lg={6}>
                      <label
                        htmlFor="category-disabled-fes"
                        className={`position-relative w-100 border rounded p-3 d-flex align-items-start gap-3 bg-light-subtle ${
                          formData.disabled_fes ? 'border-primary' : ''
                        }`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          className="visually-hidden"
                          type="checkbox"
                          id="category-disabled-fes"
                          name="disabled_fes"
                          checked={Boolean(formData.disabled_fes)}
                          onChange={handleInputChange}
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                            position: 'absolute',
                            left: '-9999px',
                          }}
                        />
                        <span
                          className={`d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 ${
                            formData.disabled_fes ? 'border-primary text-primary' : 'text-muted'
                          }`}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            background: '#fff',
                          }}
                          aria-hidden="true"
                        >
                          {formData.disabled_fes ? <IconifyIcon icon="solar:check-circle-bold" /> : null}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>Fès & alentours</div>
                          <div className="text-muted" style={{ fontSize: '13px', lineHeight: 1.35 }}>
                            {formData.disabled_fes ? 'Désactivée pour les clients de Fès' : 'Active pour les clients de Fès'}
                          </div>
                        </div>
                      </label>
                    </Col>
                    <Col lg={6}>
                      <label
                        htmlFor="category-disabled-casa"
                        className={`position-relative w-100 border rounded p-3 d-flex align-items-start gap-3 bg-light-subtle ${
                          formData.disabled_casa ? 'border-primary' : ''
                        }`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          className="visually-hidden"
                          type="checkbox"
                          id="category-disabled-casa"
                          name="disabled_casa"
                          checked={Boolean(formData.disabled_casa)}
                          onChange={handleInputChange}
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                            position: 'absolute',
                            left: '-9999px',
                          }}
                        />
                        <span
                          className={`d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 ${
                            formData.disabled_casa ? 'border-primary text-primary' : 'text-muted'
                          }`}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            background: '#fff',
                          }}
                          aria-hidden="true"
                        >
                          {formData.disabled_casa ? <IconifyIcon icon="solar:check-circle-bold" /> : null}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>Casablanca & alentours</div>
                          <div className="text-muted" style={{ fontSize: '13px', lineHeight: 1.35 }}>
                            {formData.disabled_casa
                              ? 'Désactivée pour les clients de Casablanca'
                              : 'Active pour les clients de Casablanca'}
                          </div>
                        </div>
                      </label>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-5" style={{ paddingBottom: '12px' }}>
                  <label className="form-label">Consommation sur place</label>
                  <Row className="g-4">
                    <Col lg={12}>
                      <label
                        htmlFor="category-in-store-only"
                        className={`position-relative w-100 border rounded p-3 d-flex align-items-start gap-3 bg-light-subtle ${
                          formData.in_store_only ? 'border-primary' : ''
                        }`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          className="visually-hidden"
                          type="checkbox"
                          id="category-in-store-only"
                          name="in_store_only"
                          checked={Boolean(formData.in_store_only)}
                          onChange={handleInputChange}
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                            position: 'absolute',
                            left: '-9999px',
                          }}
                        />
                        <span
                          className={`d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 ${
                            formData.in_store_only ? 'border-primary text-primary' : 'text-muted'
                          }`}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            background: '#fff',
                          }}
                          aria-hidden="true"
                        >
                          {formData.in_store_only ? <IconifyIcon icon="solar:check-circle-bold" /> : null}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>Produits à consommer sur place</div>
                          <div className="text-muted" style={{ fontSize: '13px', lineHeight: 1.35 }}>
                            {formData.in_store_only
                              ? 'Les produits de cette catégorie ne peuvent pas être commandés en ligne (panier et WhatsApp désactivés)'
                              : 'Les produits peuvent être commandés en ligne normalement'}
                          </div>
                        </div>
                      </label>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            
            <div className="p-3 bg-light mt-4 mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer la Catégorie')}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddCategory;
